import React, { useState } from 'react';
import { assessmentService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Plus, Edit2, Trash2, ChevronDown, ChevronUp,
  AlertCircle, Loader2, Save, X, Eye, CheckCircle2,
  ToggleLeft, ToggleRight, Star, RotateCcw, Search,
} from 'lucide-react';
import toast from 'react-hot-toast';

const OUTCOME_LABELS = {
  IDEA_STAGE_INNOVATOR:     'Idea Stage Innovator',
  EARLY_STAGE_ENTREPRENEUR: 'Early-Stage Entrepreneur',
  PROTOTYPE_MVP_BUILDER:    'Prototype/MVP Builder',
  GROWTH_COMMERCIALIZATION: 'Growth & Commercialization Candidate',
  ADVANCED_ENTERPRISE:      'Advanced Enterprise Support Candidate',
};

const OUTCOME_STYLES = {
  IDEA_STAGE_INNOVATOR:     'bg-blue-50 text-blue-700 border-blue-200',
  EARLY_STAGE_ENTREPRENEUR: 'bg-amber-50 text-amber-700 border-amber-200',
  PROTOTYPE_MVP_BUILDER:    'bg-orange-50 text-orange-700 border-orange-200',
  GROWTH_COMMERCIALIZATION: 'bg-forest-50 text-forest-700 border-forest-200',
  ADVANCED_ENTERPRISE:      'bg-purple-50 text-purple-700 border-purple-200',
};

// ── Question Form ─────────────────────────────────────────────
const QuestionForm = ({ areaId, existing, onSaved, onCancel }) => {
  // Safely parse options — DB returns JSON string, admin form returns array
  const parseOptions = (opts) => {
    if (!opts) return ['', '', '', ''];
    if (Array.isArray(opts)) return opts.length >= 2 ? [...opts] : [...opts, ...Array(4 - opts.length).fill('')];
    if (typeof opts === 'string') {
      try {
        const parsed = JSON.parse(opts);
        return Array.isArray(parsed) ? parsed : ['', '', '', ''];
      } catch { return ['', '', '', '']; }
    }
    return ['', '', '', ''];
  };

  const [form, setForm] = useState({
    text:          existing?.text          ?? '',
    type:          existing?.type          ?? 'MULTIPLE_CHOICE',
    weight:        existing?.weight        ?? 10,
    correctOption: existing?.correctOption ?? 0,
    options:       parseOptions(existing?.options),
    scaleLabel:    '',
  });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const setOption = (i, val) => setForm(p => {
    const opts = [...p.options]; opts[i] = val; return { ...p, options: opts };
  });

  const handleSave = async () => {
    if (!form.text.trim()) { toast.error('Question text is required'); return; }
    if (form.type === 'MULTIPLE_CHOICE') {
      const filled = form.options.filter(o => o.trim());
      if (filled.length < 2) { toast.error('Add at least 2 options'); return; }
    }
    setSaving(true);
    try {
      const payload = {
        text:   form.text,
        type:   form.type,
        weight: Number(form.weight),
        ...(form.type === 'MULTIPLE_CHOICE' && {
          options:       form.options.filter(o => o.trim()),
          correctOption: Number(form.correctOption),
        }),
      };
      if (existing?.id) {
        await assessmentService.updateQuestion(existing.id, payload);
        toast.success('Question updated');
      } else {
        await assessmentService.createQuestion(areaId, payload);
        toast.success('Question added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save question');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-blue-800 uppercase tracking-widest">
          {existing ? 'Edit Question' : 'New Question'}
        </p>
        <button onClick={onCancel} className="text-blue-400 hover:text-blue-700"><X size={14} /></button>
      </div>

      <textarea value={form.text} onChange={f('text')} rows={3}
        placeholder="Enter your question here..."
        className="w-full px-3 py-2.5 rounded-xl border border-blue-200 text-sm focus:outline-none focus:border-forest-500 resize-none bg-white" />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Type</label>
          <select value={form.type} onChange={f('type')}
            className="w-full px-3 py-2 rounded-xl border border-blue-200 text-sm bg-white focus:outline-none focus:border-forest-500">
            <option value="MULTIPLE_CHOICE">Multiple Choice</option>
            <option value="SCALE">Scale (1–5)</option>
            <option value="YES_NO">Yes / No</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Score Weight</label>
          <input type="number" value={form.weight} onChange={f('weight')} min={1} max={100}
            className="w-full px-3 py-2 rounded-xl border border-blue-200 text-sm bg-white focus:outline-none focus:border-forest-500" />
        </div>
      </div>

      {form.type === 'MULTIPLE_CHOICE' && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-forest-700">Options (mark correct answer)</label>
          {form.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" name="correct" checked={Number(form.correctOption) === i}
                onChange={() => setForm(p => ({ ...p, correctOption: i }))}
                className="flex-shrink-0 accent-forest-600" title="Mark as correct" />
              <span className="text-xs text-forest-500 w-5 flex-shrink-0 font-bold">
                {String.fromCharCode(65 + i)}
              </span>
              <input value={opt} onChange={e => setOption(i, e.target.value)}
                placeholder={`Option ${String.fromCharCode(65 + i)}`}
                className="flex-1 px-3 py-2 rounded-xl border border-blue-200 text-sm bg-white focus:outline-none focus:border-forest-500" />
            </div>
          ))}
          <p className="text-[10px] text-forest-400">Click the radio button to mark the correct answer</p>
        </div>
      )}

      {form.type === 'SCALE' && (
        <div className="bg-white border border-blue-100 rounded-xl p-3 text-xs text-forest-600">
          Scale questions use a 1–5 rating. Score is calculated proportionally (5 = full marks, 1 = minimum).
        </div>
      )}

      {form.type === 'YES_NO' && (
        <div className="bg-white border border-blue-100 rounded-xl p-3 text-xs text-forest-600">
          Yes/No questions: <strong>Yes = full marks</strong>, No = 0 points.
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving}
          className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 disabled:opacity-60">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {existing ? 'Update' : 'Add Question'}
        </button>
        <button onClick={onCancel}
          className="text-xs py-2 px-4 border border-cream-200 rounded-full text-forest-600 hover:border-forest-400">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Question Row ──────────────────────────────────────────────
const QuestionRow = ({ question, onUpdated }) => {
  const [editing,  setEditing]  = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Delete this question?')) return;
    setDeleting(true);
    try {
      await assessmentService.deleteQuestion(question.id);
      toast.success('Question removed');
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete');
    } finally { setDeleting(false); }
  };

  const handleToggle = async () => {
    try {
      await assessmentService.updateQuestion(question.id, { isActive: !question.isActive });
      toast.success(question.isActive ? 'Question deactivated' : 'Question activated');
      onUpdated();
    } catch { toast.error('Could not toggle'); }
  };

  if (editing) return (
    <QuestionForm
      areaId={question.areaId}
      existing={question}
      onSaved={() => { setEditing(false); onUpdated(); }}
      onCancel={() => setEditing(false)}
    />
  );

  return (
    <div className={`border rounded-xl p-3 ${question.isActive ? 'border-cream-200 bg-white' : 'border-cream-100 bg-cream-50 opacity-60'}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${question.isActive ? 'text-forest-900' : 'text-forest-400 line-through'}`}>
            {question.text}
          </p>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-[10px] bg-forest-50 text-forest-600 border border-forest-100 px-2 py-0.5 rounded-full font-medium">
              {question.type.replace('_', ' ')}
            </span>
            <span className="text-[10px] text-forest-400">Weight: {question.weight}</span>
            {question.type === 'MULTIPLE_CHOICE' && question.options?.length > 0 && (
              <span className="text-[10px] text-forest-400">{question.options.length} options · Correct: {String.fromCharCode(65 + question.correctOption)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={handleToggle}
            className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors"
            title={question.isActive ? 'Deactivate' : 'Activate'}>
            {question.isActive ? <ToggleRight size={17} className="text-forest-600" /> : <ToggleLeft size={17} />}
          </button>
          <button onClick={() => setEditing(true)}
            className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors">
            <Edit2 size={13} />
          </button>
          <button onClick={handleDelete} disabled={deleting}
            className="p-1.5 text-forest-300 hover:text-rust-500 rounded-lg hover:bg-rust-50 transition-colors">
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Area Card ─────────────────────────────────────────────────
const AreaCard = ({ area, onUpdated }) => {
  const [expanded,   setExpanded]   = useState(false);
  const [addingQ,    setAddingQ]    = useState(false);
  const [editingArea, setEditingArea] = useState(false);
  const [areaForm,   setAreaForm]   = useState({ name: area.name, description: area.description, purpose: area.purpose });
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(false);

  const handleSaveArea = async () => {
    if (!areaForm.name.trim()) { toast.error('Area name required'); return; }
    setSaving(true);
    try {
      await assessmentService.updateArea(area.id, areaForm);
      toast.success('Area updated');
      setEditingArea(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update area');
    } finally { setSaving(false); }
  };

  const handleDeleteArea = async () => {
    if (!window.confirm(`Delete "${area.name}"? All questions in this area will also be removed.`)) return;
    setDeleting(true);
    try {
      await assessmentService.deleteArea(area.id);
      toast.success('Area deleted');
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete area');
    } finally { setDeleting(false); }
  };

  const questions = area.questions ?? [];
  const activeQs  = questions.filter(q => q.isActive).length;

  return (
    <div className="border-2 border-cream-200 rounded-2xl overflow-hidden bg-white">
      {/* Area header */}
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-xl flex items-center justify-center text-xl flex-shrink-0">
            {area.icon || '📋'}
          </div>
          <div className="flex-1 min-w-0">
            {editingArea ? (
              <div className="space-y-2">
                <input value={areaForm.name} onChange={e => setAreaForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 font-semibold" />
                <textarea value={areaForm.description} onChange={e => setAreaForm(p => ({ ...p, description: e.target.value }))} rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
                <textarea value={areaForm.purpose} onChange={e => setAreaForm(p => ({ ...p, purpose: e.target.value }))} rows={2}
                  placeholder="Purpose of this area..."
                  className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
                <div className="flex gap-2">
                  <button onClick={handleSaveArea} disabled={saving}
                    className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1">
                    {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                  </button>
                  <button onClick={() => setEditingArea(false)}
                    className="text-xs py-1.5 px-3 border border-cream-200 rounded-full text-forest-600">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-serif font-semibold text-forest-900">{area.name}</h3>
                <p className="text-xs text-forest-500 mt-0.5 line-clamp-2">{area.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-forest-400">
                  <span>{activeQs} active question{activeQs !== 1 ? 's' : ''}</span>
                  <span>Order: {area.order}</span>
                </div>
              </>
            )}
          </div>

          {!editingArea && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => setEditingArea(true)}
                className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors">
                <Edit2 size={15} />
              </button>
              <button onClick={handleDeleteArea} disabled={deleting}
                className="p-1.5 text-forest-300 hover:text-rust-500 rounded-lg hover:bg-rust-50 transition-colors">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
              </button>
              <button onClick={() => setExpanded(!expanded)}
                className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors">
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Questions */}
      {expanded && (
        <div className="border-t border-cream-100 bg-cream-50 p-4 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest">Questions</p>
            <button onClick={() => setAddingQ(!addingQ)}
              className="flex items-center gap-1.5 text-xs font-medium text-forest-600 border border-forest-200 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors">
              <Plus size={12} /> Add Question
            </button>
          </div>

          {addingQ && (
            <QuestionForm
              areaId={area.id}
              onSaved={() => { setAddingQ(false); onUpdated(); }}
              onCancel={() => setAddingQ(false)}
            />
          )}

          {questions.length === 0 && !addingQ && (
            <div className="text-center py-6 text-forest-400">
              <p className="text-sm">No questions yet</p>
            </div>
          )}

          {questions.map(q => (
            <QuestionRow key={q.id} question={q} onUpdated={onUpdated} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Submission Card ───────────────────────────────────────────
const SubmissionCard = ({ sub, onUpdated }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes,    setNotes]    = useState(sub.adminNotes || '');
  const [saving,   setSaving]   = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const style  = OUTCOME_STYLES[sub.outcome] || '';
  const client = sub.clientProfile?.user;

  // Parse JSON fields that may come as strings from backend
  const parseJson = (val, fallback) => {
    if (!val) return fallback;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };
  const areaScores = parseJson(sub.areaScores, {});
  const strengths  = parseJson(sub.strengths,  []);
  const gapAreas   = parseJson(sub.gapAreas,   []);
  const areas = Object.values(areaScores).sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleReview = async () => {
    setSaving(true);
    try {
      await assessmentService.reviewSubmission(sub.id, { adminNotes: notes, outcome: sub.outcome });
      toast.success('Review saved');
      onUpdated?.();
    } catch { toast.error('Could not save review'); }
    finally { setSaving(false); }
  };

  const handleReject = async () => {
    if (!notes.trim()) { toast.error('Add a note explaining what needs to change first'); return; }
    if (!window.confirm(`Send this back to ${client?.firstName ?? 'the client'} to retake?`)) return;
    setRejecting(true);
    try {
      await assessmentService.rejectSubmission(sub.id, { adminNotes: notes });
      toast.success('Sent back to client for revision');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send back for revision');
    } finally { setRejecting(false); }
  };

  const STATUS_BADGE = {
    REVIEWED:            'bg-forest-50 text-forest-700 border-forest-200',
    COMPLETED:           'bg-amber-50 text-amber-700 border-amber-200',
    REVISION_REQUESTED:  'bg-rust-50 text-rust-500 border-rust-200',
  };

  return (
    <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
      <div className="p-5 flex items-start gap-4">
        <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold flex-shrink-0">
          {client?.firstName?.[0] ?? '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h4 className="font-sans font-semibold text-forest-900 text-sm">
                {client?.firstName} {client?.lastName}
              </h4>
              <p className="text-xs text-forest-400 mt-0.5">{client?.email}</p>
              <p className="text-xs text-forest-400">
                Completed: {sub.completedAt ? new Date(sub.completedAt).toLocaleDateString('en-GH', { dateStyle: 'medium' }) : '—'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="text-right">
                <div className="font-serif text-2xl font-bold text-forest-900">{sub.totalScore}</div>
                <div className="text-[10px] text-forest-400">/100</div>
              </div>
              {sub.outcome && (
                <span className={`text-[10px] font-medium border px-2 py-0.5 rounded-full ${style}`}>
                  {OUTCOME_LABELS[sub.outcome]}
                </span>
              )}
              <span className={`text-[10px] font-medium border px-2 py-0.5 rounded-full ${
                STATUS_BADGE[sub.status] ?? 'bg-cream-100 text-forest-500 border-cream-200'
              }`}>{sub.status.replace('_', ' ')}</span>
              <button onClick={() => setExpanded(!expanded)}
                className="p-1.5 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-50">
                {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-cream-100 p-5 space-y-5">
          {/* Area scores */}
          <div>
            <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-3">Area Scores</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {areas.map((area, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-forest-600 flex-1 truncate">{area.name}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-20 h-1.5 bg-cream-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${
                        area.percentage >= 70 ? 'bg-green-500' : area.percentage >= 50 ? 'bg-amber-400' : 'bg-rust-400'
                      }`} style={{ width: `${area.percentage}%` }} />
                    </div>
                    <span className={`text-xs font-bold w-8 text-right ${
                      area.percentage >= 70 ? 'text-green-600' : area.percentage >= 50 ? 'text-amber-600' : 'text-rust-500'
                    }`}>{area.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strengths & Gaps */}
          {(strengths?.length > 0 || gapAreas?.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {(() => { try { if (typeof sub.strengths === 'string') sub.strengths = JSON.parse(sub.strengths); if (typeof sub.gapAreas === 'string') sub.gapAreas = JSON.parse(sub.gapAreas); } catch {} })()}
            {strengths?.length > 0 && (
                <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                  <p className="text-xs font-semibold text-green-700 mb-2">Strengths</p>
                  {strengths.map((s, i) => <p key={i} className="text-xs text-green-600">• {s}</p>)}
                </div>
              )}
              {gapAreas?.length > 0 && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <p className="text-xs font-semibold text-amber-700 mb-2">Gap Areas</p>
                  {gapAreas.map((g, i) => <p key={i} className="text-xs text-amber-600">• {g}</p>)}
                </div>
              )}
            </div>
          )}

          {/* Admin notes */}
          <div>
            <p className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">Admin Notes</p>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Add your observations, mentorship recommendations, or level assignment notes..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button onClick={handleReview} disabled={saving || rejecting}
                className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 disabled:opacity-60">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                Mark as Reviewed
              </button>
              <button onClick={handleReject} disabled={saving || rejecting}
                className="text-xs py-2 px-4 flex items-center gap-1.5 border border-rust-300 text-rust-500 rounded-full hover:bg-rust-50 transition-colors disabled:opacity-60">
                {rejecting ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                Request Revision
              </button>
            </div>
            <p className="text-xs text-forest-400 mt-1.5">
              "Request Revision" sends this back to the client — with the note above — and lets them retake the assessment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const AdminAssessmentsPage = () => {
  const [tab,        setTab]        = useState('submissions');
  const [refetchKey, setRefetchKey] = useState(0);
  const [addingArea, setAddingArea] = useState(false);
  const [areaForm,   setAreaForm]   = useState({ name: '', description: '', purpose: '', icon: '' });
  const [savingArea, setSavingArea] = useState(false);

  const { data: areasData,  loading: areasLoading  } = useApi(() => assessmentService.getAreas(),         [refetchKey]);

  // Submissions — fetched manually (not via useApi) so we can read the
  // real total from the response's meta instead of just how many rows
  // came back on this page, and support loading further pages.
  const SUB_PAGE_SIZE = 20;
  const [submissions,    setSubmissions]    = useState([]);
  const [subTotal,       setSubTotal]       = useState(0);
  const [subPage,        setSubPage]        = useState(1);
  const [subLoading,     setSubLoading]     = useState(true);
  const [subLoadingMore, setSubLoadingMore] = useState(false);

  // Search / filters
  const [searchInput,   setSearchInput]   = useState('');
  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');

  // Debounce the search box — wait for a pause in typing before firing
  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchSubmissions = React.useCallback(async (page = 1, append = false) => {
    if (append) setSubLoadingMore(true); else setSubLoading(true);
    try {
      const res  = await assessmentService.getSubmissions({
        page, limit: SUB_PAGE_SIZE,
        search: search || undefined,
        status: statusFilter || undefined,
        outcome: outcomeFilter || undefined,
      });
      const body = res.data;
      const rows = Array.isArray(body?.data) ? body.data : (body?.data?.submissions ?? []);
      const total = body?.meta?.total ?? rows.length;
      setSubmissions(prev => (append ? [...prev, ...rows] : rows));
      setSubTotal(total);
      setSubPage(page);
    } catch {
      if (!append) setSubmissions([]);
    } finally {
      setSubLoading(false); setSubLoadingMore(false);
    }
  }, [search, statusFilter, outcomeFilter]);

  React.useEffect(() => { fetchSubmissions(1, false); }, [refetchKey, fetchSubmissions]);

  const hasMoreSubmissions = submissions.length < subTotal;
  const loadMoreSubmissions = () => fetchSubmissions(subPage + 1, true);
  const hasActiveFilters = !!(search || statusFilter || outcomeFilter);
  const clearFilters = () => { setSearchInput(''); setSearch(''); setStatusFilter(''); setOutcomeFilter(''); };

  const areas = areasData?.data?.areas ?? areasData?.areas ?? [];

  const refresh = () => setRefetchKey(k => k + 1);

  const handleCreateArea = async () => {
    if (!areaForm.name.trim()) { toast.error('Area name is required'); return; }
    setSavingArea(true);
    try {
      await assessmentService.createArea(areaForm);
      toast.success('Area created');
      setAddingArea(false);
      setAreaForm({ name: '', description: '', purpose: '', icon: '' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create area');
    } finally { setSavingArea(false); }
  };

  const totalQs = areas.reduce((s, a) => s + (a._count?.questions ?? a.questions?.length ?? 0), 0);

  return (
    <DashboardLayout title="Assessment Management">
      <div className="max-w-4xl space-y-6">

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
            <div className="font-serif text-2xl font-bold text-forest-900">{areas.length}</div>
            <div className="text-xs text-forest-500 mt-1">Assessment Areas</div>
          </div>
          <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
            <div className="font-serif text-2xl font-bold text-forest-900">{totalQs}</div>
            <div className="text-xs text-forest-500 mt-1">Total Questions</div>
          </div>
          <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
            <div className="font-serif text-2xl font-bold text-forest-900">{subTotal}</div>
            <div className="text-xs text-forest-500 mt-1">Submissions</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-cream-200">
          {[
            { id: 'submissions', label: '📋 Submissions' },
            { id: 'questions',   label: '❓ Questions & Areas' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-sm font-medium px-4 py-3 border-b-2 transition-colors ${
                tab === t.id ? 'border-forest-600 text-forest-900' : 'border-transparent text-forest-500 hover:text-forest-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Submissions */}
        {tab === 'submissions' && (
          <div className="space-y-4">

            {/* Search + filters */}
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
                <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name, email, phone, or business..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-700 focus:outline-none focus:border-forest-500 bg-white">
                <option value="">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="REVIEWED">Reviewed</option>
                <option value="REVISION_REQUESTED">Revision Requested</option>
              </select>
              <select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}
                className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-700 focus:outline-none focus:border-forest-500 bg-white">
                <option value="">All Outcomes</option>
                {Object.entries(OUTCOME_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <button onClick={clearFilters}
                  className="flex items-center gap-1.5 text-xs font-medium text-forest-500 hover:text-forest-800 px-3 py-2.5">
                  <X size={13} /> Clear
                </button>
              )}
            </div>

            {!subLoading && (
              <p className="text-xs text-forest-400">
                {hasActiveFilters ? `${subTotal} matching submission${subTotal !== 1 ? 's' : ''}` : `${subTotal} submission${subTotal !== 1 ? 's' : ''} total`}
              </p>
            )}

            {subLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 bg-white border border-cream-200 rounded-2xl animate-pulse" />
              ))
            ) : submissions.length === 0 ? (
              <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
                <Eye size={36} className="mx-auto mb-3 opacity-30" />
                <p className="font-serif text-xl text-forest-600">
                  {hasActiveFilters ? 'No matching submissions' : 'No submissions yet'}
                </p>
                <p className="text-sm mt-2">
                  {hasActiveFilters ? 'Try a different search or clear your filters' : 'Client assessment submissions will appear here'}
                </p>
              </div>
            ) : (
              <>
                {submissions.map(s => <SubmissionCard key={s.id} sub={s} onUpdated={refresh} />)}
                {hasMoreSubmissions && (
                  <div className="flex justify-center py-2">
                    <button onClick={loadMoreSubmissions} disabled={subLoadingMore}
                      className="text-sm text-forest-600 border border-cream-200 hover:border-forest-400 px-4 py-2 rounded-full transition-colors disabled:opacity-50 flex items-center gap-2">
                      {subLoadingMore && <Loader2 size={13} className="animate-spin" />}
                      Load more ({submissions.length} of {subTotal})
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Questions & Areas */}
        {tab === 'questions' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-forest-500">{areas.length} areas configured</p>
              <button onClick={() => setAddingArea(!addingArea)}
                className="btn-primary flex items-center gap-2 text-sm">
                <Plus size={15} /> Add Area
              </button>
            </div>

            {addingArea && (
              <div className="bg-white border-2 border-forest-200 rounded-2xl p-5 space-y-3">
                <h3 className="font-serif font-semibold text-forest-900">New Assessment Area</h3>
                <input value={areaForm.name} onChange={e => setAreaForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Area name e.g. Entrepreneurial Mindset"
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
                <textarea value={areaForm.description} onChange={e => setAreaForm(p => ({ ...p, description: e.target.value }))} rows={2}
                  placeholder="What does this area assess?"
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
                <textarea value={areaForm.purpose} onChange={e => setAreaForm(p => ({ ...p, purpose: e.target.value }))} rows={2}
                  placeholder="Purpose of this assessment area..."
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
                <input value={areaForm.icon} onChange={e => setAreaForm(p => ({ ...p, icon: e.target.value }))}
                  placeholder="Emoji icon e.g. 💡"
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
                <div className="flex gap-2">
                  <button onClick={handleCreateArea} disabled={savingArea}
                    className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60">
                    {savingArea ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Create Area
                  </button>
                  <button onClick={() => setAddingArea(false)}
                    className="text-sm py-2.5 px-5 border border-cream-200 rounded-full text-forest-600 hover:border-forest-400">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {areasLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-24 bg-white border-2 border-cream-200 rounded-2xl animate-pulse" />
              ))
            ) : (
              areas.map(area => <AreaCard key={area.id} area={area} onUpdated={refresh} />)
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminAssessmentsPage;